import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, Target, Briefcase, GraduationCap, Sparkles, 
  ChevronRight, ChevronLeft, Loader2, CheckCircle, 
  Edit2, Trash2, Plus, RefreshCw, Save, ArrowRight,
  User, Building2, TrendingUp, Lightbulb, Award,
  Search, Filter, BookOpen, Beaker, Trophy, Play
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const API = `${API_BASE}/api`;

// NAVI Avatar
const NAVI_AVATAR = "https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/f47qg3qu_image.png";

// Areas of Work
const AREAS_OF_WORK = [
  "Governance",
  "Risk",
  "Compliance",
  "AML / Financial Crime",
  "GRC Operations",
  "IT / Technology",
  "Cybersecurity",
  "Data",
  "Data Management",
  "Privacy",
  "AI Governance",
  "Digital Transformation",
  "Strategy",
  "Other"
];

// Experience Levels
const EXPERIENCE_LEVELS = [
  { value: "Foundation", label: "Foundation", description: "Entry-level, learning fundamentals" },
  { value: "Developing", label: "Developing", description: "Building skills, some experience" },
  { value: "Experienced", label: "Experienced", description: "Solid experience, works independently" },
  { value: "Advanced", label: "Advanced", description: "Expert level, mentors others" },
  { value: "Leadership", label: "Leadership", description: "Strategic leadership, shapes direction" }
];

// Target Levels for competencies
const TARGET_LEVELS = ["Foundation", "Applied", "Proficient", "Advanced", "Strategic"];

// Competency Categories
const COMPETENCY_CATEGORIES = [
  "Technical Competence",
  "Regulatory & Compliance Competence",
  "Digital & Technology Competence",
  "Critical Thinking & Problem Solving",
  "Professional Judgement",
  "Communication & Stakeholder Engagement",
  "Documentation & Evidence Management",
  "Ethics & Professional Conduct",
  "Behavioural & Workplace Effectiveness",
  "Leadership & Strategic Capability"
];

// Category colors
const CATEGORY_COLORS = {
  "Technical Competence": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Regulatory & Compliance Competence": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Digital & Technology Competence": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  "Critical Thinking & Problem Solving": "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Professional Judgement": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  "Communication & Stakeholder Engagement": "bg-pink-500/20 text-pink-400 border-pink-500/30",
  "Documentation & Evidence Management": "bg-slate-500/20 text-slate-400 border-slate-500/30",
  "Ethics & Professional Conduct": "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
  "Behavioural & Workplace Effectiveness": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "Leadership & Strategic Capability": "bg-rose-500/20 text-rose-400 border-rose-500/30"
};

// Level colors
const LEVEL_COLORS = {
  "Foundation": "bg-green-500/20 text-green-400",
  "Applied": "bg-blue-500/20 text-blue-400",
  "Proficient": "bg-amber-500/20 text-amber-400",
  "Advanced": "bg-purple-500/20 text-purple-400",
  "Strategic": "bg-rose-500/20 text-rose-400"
};

export default function CompetencyFrameworkGenerator() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Wizard state
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  
  // User inputs
  const [inputs, setInputs] = useState({
    current_role: '',
    area_of_work: '',
    experience_level: '',
    learning_goals: '',
    career_goal: ''
  });
  
  // Generated competencies
  const [competencies, setCompetencies] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [frameworkName, setFrameworkName] = useState('My Competency Framework');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompetency, setEditingCompetency] = useState(null);
  
  // Add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCompetency, setNewCompetency] = useState({
    name: '',
    category: '',
    definition: '',
    measurable_outcome: '',
    target_level: 'Applied',
    skills_required: [],
    evidence_of_competence: ''
  });
  
  // Saved state
  const [savedFrameworkId, setSavedFrameworkId] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Validation
  const isStep1Valid = inputs.current_role.trim().length > 0;
  const isStep2Valid = inputs.area_of_work && inputs.experience_level;
  const isStep3Valid = inputs.learning_goals.trim().length > 0 && inputs.career_goal.trim().length > 0;
  
  // Generate competencies
  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/competencies/generate`, { inputs }, { headers });
      setCompetencies(response.data.competencies || []);
      setStep(4);
      toast.success('Competencies generated successfully!');
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error('Failed to generate competencies. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Regenerate
  const handleRegenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/competencies/generate`, { inputs }, { headers });
      setCompetencies(response.data.competencies || []);
      toast.success('Competencies regenerated!');
    } catch (error) {
      console.error('Regeneration failed:', error);
      toast.error('Failed to regenerate competencies.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Save framework
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/competencies/framework/save`, {
        name: frameworkName,
        inputs,
        competencies
      }, { headers });
      
      setSavedFrameworkId(response.data.framework_id);
      toast.success('Competency framework saved!');
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save framework.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Edit competency
  const handleEdit = (competency) => {
    setEditingCompetency({ ...competency });
    setEditDialogOpen(true);
  };
  
  const handleSaveEdit = () => {
    setCompetencies(prev => 
      prev.map(c => c.competency_id === editingCompetency.competency_id ? editingCompetency : c)
    );
    setEditDialogOpen(false);
    setEditingCompetency(null);
    toast.success('Competency updated');
  };
  
  // Delete competency
  const handleDelete = (competencyId) => {
    setCompetencies(prev => prev.filter(c => c.competency_id !== competencyId));
    toast.success('Competency removed');
  };
  
  // Add competency
  const handleAdd = () => {
    const id = `comp_${Date.now().toString(36)}`;
    setCompetencies(prev => [...prev, { ...newCompetency, competency_id: id }]);
    setAddDialogOpen(false);
    setNewCompetency({
      name: '',
      category: '',
      definition: '',
      measurable_outcome: '',
      target_level: 'Applied',
      skills_required: [],
      evidence_of_competence: ''
    });
    toast.success('Competency added');
  };
  
  // Accept all
  const handleAcceptAll = () => {
    toast.success('All competencies accepted');
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500/20 to-amber-500/20 mb-4">
                <User className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">What is your current role?</h2>
              <p className="text-zinc-400">Tell us about your current job title or position</p>
            </div>
            
            <div className="max-w-xl mx-auto">
              <Label htmlFor="current_role" className="text-zinc-300 mb-2 block">Current Job Title / Role</Label>
              <Input
                id="current_role"
                placeholder="e.g., Compliance Officer, Risk Analyst, IT Governance Manager..."
                value={inputs.current_role}
                onChange={(e) => setInputs({ ...inputs, current_role: e.target.value })}
                className="bg-zinc-800/50 border-zinc-700 text-white h-12 text-lg"
                data-testid="input-current-role"
              />
              <p className="text-sm text-zinc-500 mt-2">
                Be specific about your role - this helps NAVI understand your context
              </p>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 mb-4">
                <Building2 className="h-8 w-8 text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Your Area & Experience</h2>
              <p className="text-zinc-400">Select your area of work and experience level</p>
            </div>
            
            <div className="max-w-xl mx-auto space-y-6">
              <div>
                <Label className="text-zinc-300 mb-2 block">Area of Work</Label>
                <Select
                  value={inputs.area_of_work}
                  onValueChange={(value) => setInputs({ ...inputs, area_of_work: value })}
                >
                  <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white h-12" data-testid="select-area-of-work">
                    <SelectValue placeholder="Select your area of work" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {AREAS_OF_WORK.map((area) => (
                      <SelectItem key={area} value={area} className="text-white hover:bg-zinc-800">
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300 mb-3 block">Experience Level</Label>
                <div className="grid grid-cols-1 gap-3">
                  {EXPERIENCE_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setInputs({ ...inputs, experience_level: level.value })}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        inputs.experience_level === level.value
                          ? 'bg-teal-500/20 border-teal-500 text-white'
                          : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                      }`}
                      data-testid={`level-${level.value.toLowerCase()}`}
                    >
                      <div className="font-semibold">{level.label}</div>
                      <div className="text-sm text-zinc-400">{level.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 mb-4">
                <Target className="h-8 w-8 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Your Goals</h2>
              <p className="text-zinc-400">Tell us what you want to learn and where you want to go</p>
            </div>
            
            <div className="max-w-xl mx-auto space-y-6">
              <div>
                <Label htmlFor="learning_goals" className="text-zinc-300 mb-2 block">
                  <Lightbulb className="h-4 w-4 inline mr-2 text-amber-400" />
                  Learning Goals
                </Label>
                <Textarea
                  id="learning_goals"
                  placeholder="What would you like to learn or become better at? e.g., Understanding AI risk management, improving regulatory reporting skills, learning about cyber resilience..."
                  value={inputs.learning_goals}
                  onChange={(e) => setInputs({ ...inputs, learning_goals: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-white min-h-[120px]"
                  data-testid="input-learning-goals"
                />
              </div>
              
              <div>
                <Label htmlFor="career_goal" className="text-zinc-300 mb-2 block">
                  <TrendingUp className="h-4 w-4 inline mr-2 text-teal-400" />
                  Career Goal
                </Label>
                <Textarea
                  id="career_goal"
                  placeholder="What role or area would you like to progress into? e.g., Chief Risk Officer, Head of Compliance, AI Governance Lead..."
                  value={inputs.career_goal}
                  onChange={(e) => setInputs({ ...inputs, career_goal: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-white min-h-[120px]"
                  data-testid="input-career-goal"
                />
              </div>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">Your Competency Framework</h2>
                <p className="text-zinc-400">
                  NAVI has generated {competencies.length} competencies based on your profile
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleRegenerate}
                  disabled={isGenerating}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setAddDialogOpen(true)}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>
            
            {/* Summary Card */}
            <div className="bg-gradient-to-br from-teal-500/10 to-amber-500/10 rounded-xl p-6 border border-teal-500/20">
              <div className="flex items-start gap-4">
                <img src={NAVI_AVATAR} alt="NAVI" className="w-12 h-12 rounded-full" />
                <div>
                  <h3 className="font-semibold text-white mb-2">NAVI&apos;s Analysis</h3>
                  <p className="text-sm text-zinc-300">
                    Based on your current role as <strong className="text-teal-400">{inputs.current_role}</strong> in{' '}
                    <strong className="text-teal-400">{inputs.area_of_work}</strong>, I&apos;ve identified key competencies 
                    to help you progress towards your goal of becoming a{' '}
                    <strong className="text-amber-400">{inputs.career_goal}</strong>.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Competencies List */}
            <div className="space-y-4">
              {competencies.map((competency, index) => (
                <div
                  key={competency.competency_id}
                  className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700 hover:border-zinc-600 transition-all"
                  data-testid={`competency-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{competency.name}</h3>
                        <Badge className={LEVEL_COLORS[competency.target_level] || 'bg-zinc-500/20'}>
                          {competency.target_level}
                        </Badge>
                      </div>
                      <Badge variant="outline" className={CATEGORY_COLORS[competency.category] || 'border-zinc-600'}>
                        {competency.category}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(competency)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(competency.competency_id)}
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-zinc-500 mb-1">Definition</p>
                      <p className="text-zinc-300">{competency.definition}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 mb-1">Measurable Outcome</p>
                      <p className="text-zinc-300">{competency.measurable_outcome}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 mb-1">Skills Required</p>
                      <div className="flex flex-wrap gap-1">
                        {(competency.skills_required || []).map((skill, i) => (
                          <Badge key={i} variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-zinc-500 mb-1">Evidence of Competence</p>
                      <p className="text-zinc-300">{competency.evidence_of_competence}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Actions */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
              <div className="flex items-center gap-4 mb-4">
                <Label htmlFor="framework_name" className="text-zinc-300 whitespace-nowrap">Framework Name:</Label>
                <Input
                  id="framework_name"
                  value={frameworkName}
                  onChange={(e) => setFrameworkName(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white max-w-md"
                  data-testid="input-framework-name"
                />
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleSave}
                  disabled={isSaving || competencies.length === 0}
                  className="bg-teal-500 hover:bg-teal-600 text-white flex-1"
                  data-testid="btn-save-framework"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Competency Framework
                </Button>
                
                {savedFrameworkId && (
                  <Button
                    onClick={() => {
                      toast.info(
                        <div>
                          <p className="font-semibold mb-2">Build Your Learning Path</p>
                          <p className="text-sm">Search for your competencies under Courses, Learning Labs, Masterclasses, and Campaigns. Add relevant content to your learning pathway!</p>
                        </div>,
                        { duration: 8000 }
                      );
                    }}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 flex-1"
                    data-testid="btn-build-pathway"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Build My Learning Path
                  </Button>
                )}
              </div>
              
              {savedFrameworkId && (
                <p className="text-sm text-zinc-500 mt-4 text-center">
                  <CheckCircle className="h-4 w-4 inline mr-1 text-green-400" />
                  Framework saved! Search for content matching your competencies in Courses, Learning Labs, Masterclasses, and Campaigns.
                </p>
              )}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Can proceed to next step
  const canProceed = () => {
    switch (step) {
      case 1: return isStep1Valid;
      case 2: return isStep2Valid;
      case 3: return isStep3Valid;
      default: return false;
    }
  };
  
  return (
    <Layout>
      <div className="min-h-screen bg-slate-950 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <img src={NAVI_AVATAR} alt="NAVI" className="w-12 h-12 rounded-full" />
              <div className="text-left">
                <h1 className="text-2xl font-bold text-white">Competency Framework Generator</h1>
                <p className="text-sm text-zinc-400">Powered by NAVI</p>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          {step < 4 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Step {step} of {totalSteps}</span>
                <span className="text-sm text-zinc-400">{Math.round((step / totalSteps) * 100)}%</span>
              </div>
              <Progress value={(step / totalSteps) * 100} className="h-2 bg-zinc-800" />
              
              {/* Step indicators */}
              <div className="flex justify-between mt-4">
                {['Current Role', 'Area & Level', 'Goals', 'Framework'].map((label, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      i + 1 < step ? 'bg-teal-500 text-white' :
                      i + 1 === step ? 'bg-teal-500/20 text-teal-400 border-2 border-teal-500' :
                      'bg-zinc-800 text-zinc-500'
                    }`}>
                      {i + 1 < step ? <CheckCircle className="h-4 w-4" /> : i + 1}
                    </div>
                    <span className={`text-xs mt-1 ${i + 1 <= step ? 'text-zinc-300' : 'text-zinc-600'}`}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Content */}
          <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-8">
            {renderStepContent()}
          </div>
          
          {/* Navigation */}
          {step < 4 && (
            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
                className="border-zinc-700 text-zinc-300"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="bg-teal-500 hover:bg-teal-600"
                  data-testid="btn-next"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleGenerate}
                  disabled={!canProceed() || isGenerating}
                  className="bg-gradient-to-r from-teal-500 to-amber-500 hover:from-teal-600 hover:to-amber-600"
                  data-testid="btn-generate"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      NAVI is analysing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Competencies
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Competency</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Modify the competency details below
            </DialogDescription>
          </DialogHeader>
          
          {editingCompetency && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-300">Name</Label>
                <Input
                  value={editingCompetency.name}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Category</Label>
                <Select
                  value={editingCompetency.category}
                  onValueChange={(value) => setEditingCompetency({ ...editingCompetency, category: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {COMPETENCY_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat} className="text-white">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Target Level</Label>
                <Select
                  value={editingCompetency.target_level}
                  onValueChange={(value) => setEditingCompetency({ ...editingCompetency, target_level: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {TARGET_LEVELS.map((level) => (
                      <SelectItem key={level} value={level} className="text-white">
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Definition</Label>
                <Textarea
                  value={editingCompetency.definition}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, definition: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Measurable Outcome</Label>
                <Textarea
                  value={editingCompetency.measurable_outcome}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, measurable_outcome: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Skills Required (comma-separated)</Label>
                <Input
                  value={(editingCompetency.skills_required || []).join(', ')}
                  onChange={(e) => setEditingCompetency({ 
                    ...editingCompetency, 
                    skills_required: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Evidence of Competence</Label>
                <Textarea
                  value={editingCompetency.evidence_of_competence}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, evidence_of_competence: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-teal-500 hover:bg-teal-600">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Competency</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Create a custom competency for your framework
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Name</Label>
              <Input
                value={newCompetency.name}
                onChange={(e) => setNewCompetency({ ...newCompetency, name: e.target.value })}
                placeholder="Competency name"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Category</Label>
              <Select
                value={newCompetency.category}
                onValueChange={(value) => setNewCompetency({ ...newCompetency, category: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {COMPETENCY_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-white">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-zinc-300">Target Level</Label>
              <Select
                value={newCompetency.target_level}
                onValueChange={(value) => setNewCompetency({ ...newCompetency, target_level: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {TARGET_LEVELS.map((level) => (
                    <SelectItem key={level} value={level} className="text-white">
                      {level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-zinc-300">Definition</Label>
              <Textarea
                value={newCompetency.definition}
                onChange={(e) => setNewCompetency({ ...newCompetency, definition: e.target.value })}
                placeholder="What this competency means"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Measurable Outcome</Label>
              <Textarea
                value={newCompetency.measurable_outcome}
                onChange={(e) => setNewCompetency({ ...newCompetency, measurable_outcome: e.target.value })}
                placeholder="What the user must demonstrate"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Skills Required (comma-separated)</Label>
              <Input
                value={(newCompetency.skills_required || []).join(', ')}
                onChange={(e) => setNewCompetency({ 
                  ...newCompetency, 
                  skills_required: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                })}
                placeholder="skill1, skill2, skill3"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Evidence of Competence</Label>
              <Textarea
                value={newCompetency.evidence_of_competence}
                onChange={(e) => setNewCompetency({ ...newCompetency, evidence_of_competence: e.target.value })}
                placeholder="How Cerebro determines achievement"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button 
              onClick={handleAdd} 
              disabled={!newCompetency.name || !newCompetency.category}
              className="bg-teal-500 hover:bg-teal-600"
            >
              Add Competency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
